import { FC, useEffect } from 'react';
import { ClipboardData, ClipboardDataType, Workspace } from 'types/Clipboard';
import { isInputFocused } from 'utils/clipboard';
import {
  addEffectsToScene,
  deleteEffectsFromScene,
  deleteScene,
  getEffects
} from 'modules/effects';
import { last } from 'ramda';
import { nanoid } from 'nanoid/non-secure';
import {
  addSceneWithData,
  getCurrentScene,
  getScenes,
  setCurrentScene,
  setCurrentSceneByTimeline
} from 'modules/scenes';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentWorkspace, setCurrentWorkspace } from 'modules/workspace';
import { getCurrentTextId, setCurrentTextId } from 'modules/currentTextId';
import analytics, { Shortcuts } from 'utils/analytics';
import { ToolName } from 'types/AppState';
import { ARROW_TEXT_MOVE_STEP_LARGE } from 'constants/textTool';
import { cloneSceneSource } from 'modules/scenesLoader';
import { applyNextRevision, applyPreviousRevision, saveRevision } from 'modules/revisions';
import { isModalOpened } from 'shared/Modal';
import { SceneType } from 'types/Scene';
import { usePlayer } from 'utils/PlayerContext';
import { EMPTY_TRANSITION } from 'constants/scenes';

export const ShortcutsClipboard: FC = () => {
  const dispatch = useDispatch();
  const scenes = useSelector(getScenes);
  const currentScene = useSelector(getCurrentScene);
  const currentSceneId = currentScene?.id;
  const currentWorkspace = useSelector(getCurrentWorkspace);
  const effects = useSelector(getEffects);
  const sceneEffects = currentSceneId ? effects[currentSceneId] : [];
  const currentTextIds = useSelector(getCurrentTextId);
  const player = usePlayer();

  // Text shortcuts
  useEffect(() => {
    if (currentTextIds.length === 0 || currentWorkspace !== Workspace.Artboard) return;

    const onEsc = (evt: KeyboardEvent) => {
      if (evt.code === 'Escape' && !evt.repeat) {
        dispatch(setCurrentTextId([]));
        analytics.shortcut({ item: ToolName.Text, hotkey_command: Shortcuts.Unselect });
      }
      if (evt.code === 'Backspace' && !evt.repeat && !isInputFocused() && !isModalOpened()) {
        dispatch(deleteEffectsFromScene(currentTextIds, currentScene.id));
        analytics.shortcut({ item: ToolName.Text, hotkey_command: Shortcuts.TextDelete });
        dispatch(saveRevision(ToolName.Text));
      }
    };
    window.addEventListener('keydown', onEsc);
    return () => {
      window.removeEventListener('keydown', onEsc);
    };
  }, [currentTextIds, currentWorkspace]);

  // Copy/cut text effect
  useEffect(() => {
    if (
      !(
        currentWorkspace === Workspace.Artboard &&
        currentTextIds.length > 0 &&
        sceneEffects &&
        sceneEffects.some(effect => currentTextIds.includes(effect.id))
      )
    ) {
      return;
    }

    const copyFunc = (clipboardEvent: ClipboardEvent, dispatchAnalytics: boolean = true) => {
      if (isInputFocused() || isModalOpened()) return;
      clipboardEvent.clipboardData.setData(
        'application/json',
        JSON.stringify({
          type: ClipboardDataType.Effects,
          effects: sceneEffects.filter(effect => currentTextIds.includes(effect.id)),
          from: currentSceneId
        })
      );
      clipboardEvent.clipboardData.setData(
        'text/plain',
        sceneEffects
          .filter(effect => currentTextIds.includes(effect.id))
          .map(effect => effect.text)
          .join('\n')
      );
      clipboardEvent.preventDefault();
      dispatchAnalytics &&
        analytics.shortcut({ item: ToolName.Text, hotkey_command: Shortcuts.TextCopy });
    };

    const cutFunc = (clipboardEvent: ClipboardEvent) => {
      if (isInputFocused() || isModalOpened()) return;
      copyFunc(clipboardEvent, false);
      dispatch(deleteEffectsFromScene(currentTextIds, currentSceneId));
      dispatch(saveRevision(ToolName.Text));
      analytics.shortcut({ item: ToolName.Text, hotkey_command: Shortcuts.TextCut });
    };

    document.addEventListener('copy', copyFunc);
    document.addEventListener('cut', cutFunc);

    return () => {
      document.removeEventListener('copy', copyFunc);
      document.removeEventListener('cut', cutFunc);
    };
  }, [currentTextIds, sceneEffects, currentScene, currentWorkspace]);

  // Paste text effect
  useEffect(() => {
    if (
      currentScene.type === SceneType.EMPTY ||
      (currentWorkspace !== Workspace.ScenesPanel && currentWorkspace !== Workspace.Artboard)
    ) {
      return;
    }

    const pasteFunc = (e: ClipboardEvent) => {
      if (isInputFocused() || isModalOpened()) return;
      const rawData = e.clipboardData.getData('application/json');

      let data: ClipboardData;

      try {
        data = JSON.parse(rawData);
      } catch {
        return;
      }

      if (data.type === ClipboardDataType.Effects && data.effects.length > 0) {
        data.effects.forEach(effect => {
          effect.id = nanoid();
          if (data?.from === currentSceneId) {
            effect.position.x += ARROW_TEXT_MOVE_STEP_LARGE;
            effect.position.y += ARROW_TEXT_MOVE_STEP_LARGE;
          }
        });
        dispatch(addEffectsToScene(data.effects, currentSceneId));
        dispatch(setCurrentTextId(data.effects.map(effect => effect.id)));
        dispatch(saveRevision(ToolName.Text));
        dispatch(setCurrentWorkspace(Workspace.Artboard));
        analytics.shortcut({ item: ToolName.Text, hotkey_command: Shortcuts.TextPaste });
      }
    };

    document.addEventListener('paste', pasteFunc);
    return () => {
      document.removeEventListener('paste', pasteFunc);
    };
  }, [currentSceneId, currentWorkspace, currentScene]);

  // Copy scene
  useEffect(() => {
    if (currentWorkspace !== Workspace.ScenesPanel) return;

    const copyFunc = (clipboardEvent: ClipboardEvent) => {
      if (!isInputFocused() && !isModalOpened()) {
        clipboardEvent.clipboardData.setData(
          'application/json',
          JSON.stringify({
            type: ClipboardDataType.Scene,
            scene: currentScene,
            effects: effects[currentScene.id]
          })
        );
        clipboardEvent.preventDefault();
        analytics.shortcut({ item: ToolName.Scene, hotkey_command: Shortcuts.SceneCopy });
      }
    };

    document.addEventListener('copy', copyFunc);
    return () => {
      document.removeEventListener('copy', copyFunc);
    };
  }, [currentScene, effects, currentWorkspace]);

  // Key events
  useEffect(() => {
    if (currentWorkspace !== Workspace.ScenesPanel) return;

    const onKeyDown = (evt: KeyboardEvent) => {
      if (evt.repeat || isInputFocused() || isModalOpened()) return;
      if (evt.code === 'Backspace' || evt.code === 'Delete') {
        dispatch(deleteScene(currentScene.id));
        analytics.shortcut({ item: ToolName.Scene, hotkey_command: Shortcuts.SceneDelete });
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [currentWorkspace, currentScene]);

  useEffect(() => {
    const onKeydown = (evt: KeyboardEvent) => {
      if (evt.repeat || isInputFocused() || isModalOpened()) return;
      if (evt.code === 'KeyK') {
        player.pause();
        dispatch(setCurrentSceneByTimeline());
        analytics.shortcut({ item: ToolName.Timeline, hotkey_command: Shortcuts.Pause });
      }
      if (evt.code === 'KeyL') {
        player.play();
        analytics.shortcut({ item: ToolName.Timeline, hotkey_command: Shortcuts.Play });
      }
      if (evt.code === 'KeyZ' && evt.metaKey) {
        if (evt.shiftKey) {
          dispatch(applyNextRevision());
          analytics.shortcut({ item: ToolName.UndoRedo, hotkey_command: Shortcuts.Redo });
        } else {
          dispatch(applyPreviousRevision());
          analytics.shortcut({ item: ToolName.UndoRedo, hotkey_command: Shortcuts.Undo });
        }
      }
      if (evt.code === 'KeyY' && evt.metaKey) {
        dispatch(applyNextRevision());
        analytics.shortcut({ item: ToolName.UndoRedo, hotkey_command: Shortcuts.Redo });
      }
    };
    window.addEventListener('keydown', onKeydown);
    return () => {
      window.removeEventListener('keydown', onKeydown);
    };
  }, [player]);

  // Paste scene
  useEffect(() => {
    const pasteFunc = (e: ClipboardEvent) => {
      if (currentWorkspace !== Workspace.ScenesPanel || isInputFocused() || isModalOpened()) return;
      const rawData = e.clipboardData.getData('application/json');

      let data: ClipboardData;

      try {
        data = JSON.parse(rawData);
      } catch {
        return;
      }

      if (data.type === ClipboardDataType.Scene && data.scene) {
        const lastScene = last(scenes);
        const id = nanoid();
        dispatch(cloneSceneSource(data.scene.id, id));
        data.scene.id = id;
        data.scene.transition = EMPTY_TRANSITION;
        dispatch(addSceneWithData(data.scene));
        data.effects && dispatch(addEffectsToScene(data.effects, data.scene.id));
        dispatch(setCurrentScene(data.scene.id));
        player.setTime(lastScene.start + lastScene.duration + 1);
        dispatch(saveRevision(ToolName.Scene));
        analytics.shortcut({ item: ToolName.Scene, hotkey_command: Shortcuts.ScenePaste });
      }
    };

    document.addEventListener('paste', pasteFunc);

    return () => {
      document.removeEventListener('paste', pasteFunc);
    };
  }, [scenes, currentWorkspace, player]);

  return null;
};
